/**
 * Generates star rating HTML based on a score (out of 10)
 * @param {number} score - Score out of 10
 * @returns {string} - Star rating HTML
 */
export const generateStars = (score) => {
    const fullStars = Math.floor(score / 2);
    const halfStar = (score % 2 >= 1) ? 1 : 0;
    const emptyStars = 5 - fullStars - halfStar;

    const svgFullStar = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><g clip-path="url(#a)"><path fill="#FFA645" d="m14.925 5.975-4.525-.7-2.05-4.3c-.15-.3-.55-.3-.7 0L5.6 5.3l-4.5.675c-.325.05-.45.475-.2.7l3.275 3.375-.775 4.725c-.05.325.275.6.575.4L8.05 12.95l4.05 2.225c.275.15.625-.1.55-.4l-.775-4.725 3.275-3.375c.2-.225.1-.65-.225-.7Z"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>`;
    const svgHalfStar = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><g clip-path="url(#a)"><mask id="b" width="8" height="16" x="0" y="0" maskUnits="userSpaceOnUse" style="mask-type:alpha"><path fill="#D9D9D9" d="M8 0H0v16h8z"/></mask><g mask="url(#b)"><path fill="#FFA645" d="m15.157 6.225-4.525-.7-2.05-4.3c-.15-.3-.55-.3-.7 0L5.832 5.55l-4.5.675c-.325.05-.45.475-.2.7L4.407 10.3l-.775 4.725c-.05.325.275.6.575.4L8.282 13.2l4.05 2.225c.275.15.625-.1.55-.4l-.775-4.725 3.275-3.375c.2-.225.1-.65-.225-.7Z"/></g><path fill="#FFA645" d="M4.025 15.55c-.225 0-.45-.075-.625-.2-.35-.25-.55-.7-.475-1.125l.65-4.025-2.8-2.875c-.3-.3-.4-.75-.275-1.175.125-.4.475-.7.875-.75l3.875-.625L7 1.1c.2-.4.575-.65 1-.65.425 0 .825.25 1 .65l1.75 3.65 3.85.6c.4.075.75.35.875.75.15.425.025.875-.275 1.175l-2.775 2.9.65 4.05c.075.45-.1.875-.475 1.125-.35.25-.775.275-1.15.075L8 13.55l-3.45 1.875c-.15.1-.35.125-.525.125ZM1.575 6.5v.05l2.925 3c.175.175.25.45.225.7l-.675 4.175v.025l3.6-1.95a.796.796 0 0 1 .75 0l3.6 1.95v-.025l-.675-4.2c-.05-.25.05-.5.225-.7l2.925-3c.025-.025 0-.05 0-.05L10.45 5.85c-.25-.05-.475-.2-.575-.45L8 1.6 6.2 5.425c-.1.225-.325.4-.575.45l-4.05.625Z"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>`;
    const svgEmptyStar = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><g clip-path="url(#a)"><path fill="#FFA645" d="M4.025 15.55c-.225 0-.45-.075-.625-.2-.35-.25-.55-.7-.475-1.125l.65-4.025-2.8-2.875c-.3-.3-.4-.75-.275-1.175.125-.4.475-.7.875-.75l3.875-.625L7 1.1c.2-.4.575-.65 1-.65.425 0 .825.25 1 .65l1.75 3.65 3.85.6c.4.075.75.35.875.75.15.425.025.875-.275 1.175l-2.775 2.9.65 4.05c.075.45-.1.875-.475 1.125-.35.25-.775.275-1.15.075L8 13.55l-3.45 1.875c-.15.1-.35.125-.525.125ZM1.575 6.5v.05l2.925 3c.175.175.25.45.225.7l-.675 4.175v.025l3.6-1.95a.796.796 0 0 1 .75 0l3.6 1.95v-.025l-.675-4.2c-.05-.25.05-.5.225-.7l2.925-3c.025-.025 0-.05 0-.05L10.45 5.85c-.25-.05-.475-.2-.575-.45L8 1.6 6.2 5.425c-.1.225-.325.4-.575.45l-4.05.625Z"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>`;

    return `
        ${`<span class="star full w-4" aria-hidden="true">${svgFullStar}</span>`.repeat(fullStars)}
        ${halfStar ? `<span class="star half w-4" aria-hidden="true">${svgHalfStar}</span>` : ''}
        ${`<span class="star empty w-4" aria-hidden="true">${svgEmptyStar}</span>`.repeat(emptyStars)}
    `;
};

/**
 * Creates a category score element with stars, title, and value
 * @param {string} title - The title of the score
 * @param {number} score - The numeric score
 * @param {String} classes - Optional classes for styling purposes
 * @param {boolean} hideTitle - hide or show the title
 * @returns {HTMLElement} - A DOM element for the category score
 */
const createCategoryScoreElement = (title, score, classes, hideTitle) => {
    const wrapper = document.createElement("div");
    wrapper.className = `category-score flex flex-col items-start ${classes}`;

    wrapper.innerHTML = `
        <p class="mb-2 text-lg font-medium ${hideTitle ? "invisible" : ""}">${title}</p>
        <div class="flex gap-2 items-center">
            <div class="flex gap-1 w-full" aria-label="Score: ${score}/10">
                ${generateStars(score)}
            </div>
            <span class="nunito-500 text-xl">${score.toFixed(1).replace('.', ',')}</span>
        </div>
    `;

    return wrapper;
};

/**
 * Renders category scores into a container, sorted by the "sort" property
 * @param {Array} scores - List of category scores [{ name, value, sort }]
 * @param {String} classes - Optional classes for styling purposes
 * @param {boolean} hideTitle - hide or show the title
 */

export const renderCategoryScores = (scores, classes, hideTitle = false) => {
    const fragment = document.createDocumentFragment();

    // sort scores by the "sort" property
    scores
        .sort((a, b) => a.sort - b.sort) // ascending order
        .forEach(({ name, score }) => {
            const scoreElement = createCategoryScoreElement(name, score, classes, hideTitle);
            fragment.appendChild(scoreElement);
        });

    return fragment;
};